import { useState, useEffect, memo } from 'react';

import { API_BASE_URL } from '../config/api';

const BACKEND_URL = API_BASE_URL;

const SparklineSVG = memo(({ points, width = 120, height = 40, isPositive }) => {
  if (!points || points.length < 2) return null;

  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;

  const coords = points.map((p, i) => ({
    x: (i / (points.length - 1)) * width,
    y: height - ((p - min) / range) * (height - 4) - 2,
  }));

  const pathD = coords.map((c, i) => `${i === 0 ? 'M' : 'L'} ${c.x.toFixed(1)} ${c.y.toFixed(1)}`).join(' ');
  const fillD = `${pathD} L ${width} ${height} L 0 ${height} Z`;
  const color = isPositive ? '#22c55e' : '#ef4444';
  const fillColor = isPositive ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)';

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="block">
      <defs>
        <linearGradient id={`grad-${isPositive ? 'up' : 'down'}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.15" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={fillD} fill={`url(#grad-${isPositive ? 'up' : 'down'})`} />
      <path d={pathD} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={coords[coords.length - 1].x} cy={coords[coords.length - 1].y} r="2" fill={color} />
    </svg>
  );
});

SparklineSVG.displayName = 'SparklineSVG';

const sparklineCache = {};

export const SparklineChart = ({ symbol, isPositive }) => {
  const [points, setPoints] = useState(sparklineCache[symbol] || null);

  useEffect(() => {
    if (sparklineCache[symbol]) {
      setPoints(sparklineCache[symbol]);
      return;
    }

    let cancelled = false;
    const fetchData = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/sparkline/${symbol}`);
        const data = await res.json();
        if (!cancelled && data.points?.length > 0) {
          sparklineCache[symbol] = data.points;
          setPoints(data.points);
        }
      } catch {
        // silent
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [symbol]);

  if (!points) return <div className="w-[120px] h-[40px]" />;

  return <SparklineSVG points={points} isPositive={isPositive} />;
};

export default SparklineChart;
